import { useMemo } from "react";
import { RouteStatus } from "../pages/dashboard/route-planner/helpers/route-status.enum";
import { ControlledLabelSelectOption } from "ui";
import { AdminRouteV2ControllerGetRoutesResponse } from "service";
import {
  buildPhaseRouteTypeSelectOptions,
  endedPhaseRouteTypeSelectOptions,
  livePhaseRouteTypeSelectOptions,
  mapActionRouteTypeSelectOptions,
  planPhaseRouteTypeSelectOptions,
  MapActionTypeEnum,
} from "service/types/enum/route-planner-action-type-enum";
import { MapMarker } from "../components/map/map.types";
import { isUnassignedAddress } from "../shared/utils/address-assignment";

export const routeOptions: Record<RouteStatus, ControlledLabelSelectOption> = {
  [RouteStatus.Live]: {
    value: RouteStatus.Live,
    label: "Daily Shift",
    status: "Live",
  },
  [RouteStatus.Planning]: {
    value: RouteStatus.Planning,
    label: "Daily Shift",
    status: "Planning",
  },
  [RouteStatus.Planned]: {
    value: RouteStatus.Planned,
    label: "Daily Shift",
    status: "Planned",
  },
  [RouteStatus.Ended]: {
    value: RouteStatus.Ended,
    label: "Daily Shift",
    status: "Ended",
  },
  [RouteStatus.Berm]: { value: RouteStatus.Berm, label: "Berm Route" },
};

export function useFilteredRouteOptions(
  routesData: AdminRouteV2ControllerGetRoutesResponse | undefined,
) {
  const filteredOptions = useMemo(() => {
    if (!routesData?.data || routesData.data.length === 0) {
      return [];
    }
    const routeStatus = routesData.data[0].state as RouteStatus;
    return routeStatus in routeOptions ? [routeOptions[routeStatus]] : [];
  }, [routesData]);

  const currentRouteStatus = useMemo(() => {
    return filteredOptions.length > 0
      ? (filteredOptions[0].value as RouteStatus)
      : undefined;
  }, [filteredOptions]);

  return { filteredOptions, currentRouteStatus };
}

/**
 * Helper function to check if all selected addresses have the same status
 */
function allAddressesHaveStatus(
  selectedAddresses: MapMarker[],
  targetStatus: string,
): boolean {
  if (selectedAddresses.length === 0) return false;

  return selectedAddresses.every(
    (address) => address.order?.status === targetStatus,
  );
}

/**
 * Helper function to check if any selected address currently has the given
 * order status
 */
function anyAddressHasStatus(
  selectedAddresses: MapMarker[],
  targetStatus: string,
): boolean {
  return selectedAddresses.some(
    (address) => address.order?.status === targetStatus,
  );
}

export function getBulkActions(
  routeStatus?: RouteStatus,
  selectedAddresses: MapMarker[] = [],
): ControlledLabelSelectOption[] {
  // Check if all selected addresses are unassigned
  const hasSelectedAddresses = selectedAddresses.length > 0;
  const allAddressesUnassigned =
    hasSelectedAddresses && selectedAddresses.every(isUnassignedAddress);

  // If only unassigned addresses are selected, return only Move Addresses and Send Notification
  if (allAddressesUnassigned) {
    return [
      {
        label: "Move Addresses",
        value: MapActionTypeEnum.moveAddresses,
      },
      {
        label: "Send Notification",
        value: MapActionTypeEnum.sendNotification,
      },
    ];
  }

  let baseOptions: ControlledLabelSelectOption[] = [];

  switch (routeStatus) {
    case RouteStatus.Planning:
      baseOptions = planPhaseRouteTypeSelectOptions;
      break;
    case RouteStatus.Planned:
      baseOptions = buildPhaseRouteTypeSelectOptions;
      break;
    case RouteStatus.Live:
      baseOptions = livePhaseRouteTypeSelectOptions;
      break;
    case RouteStatus.Ended:
      baseOptions = endedPhaseRouteTypeSelectOptions;
      break;
    default:
      baseOptions = mapActionRouteTypeSelectOptions;
  }

  // Filter out options based on conditions
  let filteredOptions = baseOptions;

  // For planning phase, filter out options that match current statuses of selected addresses
  if (routeStatus === RouteStatus.Planning && selectedAddresses.length > 0) {
    filteredOptions = filteredOptions.filter((option) => {
      // Only hide option if ALL selected addresses have that status
      switch (option.value) {
        case MapActionTypeEnum.moveToRegular:
          return !allAddressesHaveStatus(selectedAddresses, "regular");
        case MapActionTypeEnum.moveToDelayed:
          return !allAddressesHaveStatus(selectedAddresses, "delayed");
        case MapActionTypeEnum.moveToSkipped:
          return !allAddressesHaveStatus(selectedAddresses, "skipped");
        default:
          return true;
      }
    });
  }

  // Filter out "Reverse Sequence" action when only one address is selected
  if (selectedAddresses.length === 1) {
    filteredOptions = filteredOptions.filter(
      (option) => option.value !== MapActionTypeEnum.reverseSequence,
    );
  }

  if (routeStatus === RouteStatus.Live) {
    const firstAddress = selectedAddresses[0];
    const sameRouteAndShift =
      Boolean(firstAddress?.routeId && firstAddress.order?.status) &&
      selectedAddresses.every(
        (address) =>
          address.routeId === firstAddress.routeId &&
          address.order?.status === firstAddress.order?.status,
      );
    const sequences = selectedAddresses
      .map((address) => address.locationSequence)
      .sort((a, b) => (a ?? 0) - (b ?? 0));
    const consecutiveSequence = sequences.every(
      (sequence, index) =>
        typeof sequence === "number" &&
        Number.isInteger(sequence) &&
        sequence > 0 &&
        (index === 0 || sequence === sequences[index - 1]! + 1),
    );
    const canReverseSequence =
      selectedAddresses.length > 1 && sameRouteAndShift && consecutiveSequence;

    if (!canReverseSequence) {
      filteredOptions = filteredOptions.map((option) =>
        option.value === MapActionTypeEnum.reverseSequence
          ? {
              ...option,
              disabled: true,
              tooltip:
                "Sequence cannot be reversed for addresses that are not in sequence or not part of the same shift and route.",
            }
          : option,
      );
    }
  }

  // A skipped order must be restored to the route ("Mark as To Be Plowed")
  // before any other driver-status action can apply to it.
  if (anyAddressHasStatus(selectedAddresses, "skipped")) {
    const restrictedWhenSkipped: string[] = [
      MapActionTypeEnum.markAsCompleted,
      MapActionTypeEnum.markAsPartiallyCompleted,
      MapActionTypeEnum.markAsCantPlow,
    ];
    filteredOptions = filteredOptions.filter(
      (option) => !restrictedWhenSkipped.includes(option.value),
    );
  }

  return filteredOptions;
}
